import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuth } from '@/auth';
import LanguageSelector from '@/components/LanguageSelector';
import { useLanguage } from '@/contexts/LanguageContext';

const LOGO_URL = '/logo.png';

export function AppHeader({ right, hideBrand=false, hideBack=false }){
  const nav = useNavigate();
  const { user, role, token, loading, logout: authLogout } = useAuth();
  const { t } = useLanguage();
  
  const isAuthenticated = !!token;
  const effectiveRole = role || (isAuthenticated && loading ? 'loading' : null);
  
  const logout = ()=>{ 
    authLogout();
  };

  return (
    <header className="w-full border-b bg-white/95 backdrop-blur-sm sticky top-0 z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <div className="flex items-center gap-3">
            {!hideBack && (
              <Button data-testid="nav-back-button" variant="ghost" size="icon" onClick={()=>nav(-1)} aria-label="Torna indietro">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            {!hideBrand && (
              <Link to="/" className="flex-shrink-0" data-testid="nav-logo-link">
                <img src={LOGO_URL} alt="ServiceGo" className="h-12 sm:h-16 w-auto" data-testid="nav-logo" />
              </Link>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {right}
            <LanguageSelector />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="nav-menu-trigger" variant="outline" size="icon" aria-label="Menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={6} className="min-w-48 bg-white border border-slate-200 shadow-xl rounded-xl z-50" data-testid="nav-menu" style={{backgroundColor:'#ffffff'}}>
                {effectiveRole === 'loading' && (
                  <DropdownMenuItem disabled>Caricamento...</DropdownMenuItem>
                )}
                {!effectiveRole && (
                  <>
                    <DropdownMenuItem asChild><Link data-testid="nav-home" to="/">Home</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-login" to="/login">Login</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-register-client" to="/cliente/registrazione">{t('nav.registerAsClient')}</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-register-company" to="/ditta/registrazione">{t('nav.registerAsCompany')}</Link></DropdownMenuItem>
                  </>
                )}
                {effectiveRole==='client' && (
                  <>
                    <DropdownMenuItem asChild><Link data-testid="nav-client-dash" to="/cliente/dashboard">Dashboard Cliente</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-client-new" to="/cliente/nuova">Nuova Richiesta</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-client-settings" to="/cliente/impostazioni">Impostazioni</Link></DropdownMenuItem>
                    <DropdownMenuItem data-testid="nav-logout" onClick={logout}>Logout</DropdownMenuItem>
                  </>
                )}
                {effectiveRole==='company' && (
                  <>
                    <DropdownMenuItem asChild><Link data-testid="nav-company-dash" to="/ditta/dashboard">Dashboard Ditta</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-company-req" to="/ditta/richieste">Richieste</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-company-sub" to="/ditta/abbonamento">Abbonamento</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link data-testid="nav-company-settings" to="/ditta/impostazioni">Impostazioni</Link></DropdownMenuItem>
                    <DropdownMenuItem data-testid="nav-logout" onClick={logout}>Logout</DropdownMenuItem>
                  </>
                )}
                {effectiveRole==='admin' && (
                  <>
                    <DropdownMenuItem asChild><Link data-testid="nav-admin-dash" to="/admin">Dashboard Admin</Link></DropdownMenuItem>
                    <DropdownMenuItem data-testid="nav-logout" onClick={logout}>Logout</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}

export function AppLayout({ children, right, hideBrand=false, hideBack=false, narrow=false }){
  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader right={right} hideBrand={hideBrand} hideBack={hideBack} />
      <main className={`mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 ${narrow ? 'max-w-2xl' : 'max-w-7xl'}`}>{children}</main>
    </div>
  );
}

export default AppLayout;
